import { lazy } from 'react';
import Layout from '../shared/components/layout';
import ROUTE_PATHS from '../routes/route-paths';
import { RouteObject } from 'react-router-dom';
import HomePage from '@/pages/home';
import ProtectedRoute from './protected-route';
import { UserAuthorityRole } from '@/shared/models/auth/auth-data';

const PageLogin = lazy(() => import(/* webpackChunkName: "login" */ '@/pages/login'));
const PageNotFound = lazy(() => import(/* webpackChunkName: "404" */ '@/pages/404'));
const CabinetsPage = lazy(() => import(/* webpackChunkName: "cabinet" */ '@/pages/cabinets'));
const UnassignedCabinetsPage = lazy(
  () => import(/* webpackChunkName: "unassigned-cabinets" */ '@/pages/cabinets/unassigned-cabinets-list')
);
const AddCabinetPage = lazy(() => import(/* webpackChunkName: "add-cabinet" */ '@/pages/cabinets/add-cabinet'));
const AssignCabinetPage = lazy(
  () => import(/* webpackChunkName: "assign-cabinet" */ '@/pages/cabinets/assign-cabinet')
);
const EditCabinetPage = lazy(() => import(/* webpackChunkName: "edit-cabinet" */ '@/pages/cabinets/edit-cabinet'));

const DatasetsPage = lazy(() => import(/* webpackChunkName: "datasets" */ '@/pages/datasets/index'));
const DatasetReviewPage = lazy(() => import(/* webpackChunkName: "datasets" */ '@/pages/datasets/review-dataset'));
const ProductQAPage = lazy(() => import(/* webpackChunkName: "product-qa" */ '@/pages/product-qa'));
const EditProductPage = lazy(() => import(/* webpackChunkName: "edit-product" */ '@/pages/product-qa/edit-product'));

const FindTerminalsPage = lazy(() => import(/* webpackChunkName: "terminals" */ '@/pages/terminals/find-terminal'));
const EditTerminalPage = lazy(() => import(/* webpackChunkName: "edit-terminal" */ '@/pages/terminals/edit-terminal'));
const CreateTerminalPage = lazy(
  () => import(/* webpackChunkName: "create-terminal" */ '@/pages/terminals/create-terminal')
);
const ValidationProfilesPage = lazy(
  () => import(/* webpackChunkName: "validation-profiles" */ '@/pages/fallback/validation-profiles')
);
const CreateValidationProfilePage = lazy(
  () => import(/* webpackChunkName: "create-validation-profile" */ '@/pages/fallback/create-validation-profile')
);
const EditValidationProfilePage = lazy(
  () => import(/* webpackChunkName: "edit-validation-profile" */ '@/pages/fallback/edit-validation-profile')
);
const FallbackUsersPage = lazy(
  () => import(/* webpackChunkName: "fallback-users" */ '@/pages/fallback/fallback-users')
);
const EditFallbackUserPage = lazy(
  () => import(/* webpackChunkName: "edit-fallback-user" */ '../pages/fallback/edit-fallback-user')
);
const ForgotPasswordPage = lazy(
  () => import(/* webpackChunkName: "forgot-password" */ '@/pages/login/forgot-password')
);
const ForgotPasswordConfirmationPage = lazy(
  () => import(/* webpackChunkName: "forgot-password-confirmation" */ '@/pages/login/forgot-password-confirmation')
);
const SetPasswordPage = lazy(() => import(/* webpackChunkName: "set-password" */ '@/pages/login/set-password'));
const SetNewUserPasswordPage = lazy(
  () => import(/* webpackChunkName: "set-new-user-password" */ '@/pages/login/set-new-user-password')
);
const SetPasswordConfirmationPage = lazy(
  () => import(/* webpackChunkName: "set-password-confirmation" */ '@/pages/login/set-password-confirmation')
);
const StoreProjectPage = lazy(() => import(/* webpackChunkName: "store-project" */ '@/pages/store-project'));
const CreateStoreProjectPage = lazy(
  () => import(/* webpackChunkName: "create-store-project" */ '@/pages/store-project/create-store-project')
);
const EditStoreProjectPage = lazy(
  () => import(/* webpackChunkName: "edit-store-project" */ '@/pages/store-project/edit-store-project')
);
const EditProfilePage = lazy(() => import(/* webpackChunkName: "edit-profile" */ '@/pages/profile/edit-profile'));
const SenseiUsersPage = lazy(() => import(/* webpackChunkName: "sensei-user" */ '@/pages/users'));
const CreateSenseiUserPage = lazy(
  () => import(/* webpackChunkName: "create-user" */ '@/pages/users/create-sensei-user')
);
const EditSenseiUserPage = lazy(() => import(/* webpackChunkName: "edit-user" */ '@/pages/users/edit-sensei-user'));
const SessionsPage = lazy(() => import(/* webpackChunkName: "sessions" */ '@/pages/sessions'));
const PlanogramTasksPage = lazy(() => import(/* webpackChunkName: "planogram-tasks" */ '@/pages/planogram/tasks'));
const PlanogramTaskDetailPage = lazy(
  () => import(/* webpackChunkName: "planogram-task-detail" */ '@/pages/planogram/task-detail')
);
const PlanogramValidatedTasksPage = lazy(
  () => import(/* webpackChunkName: "planogram-validated-tasks" */ '@/pages/planogram/validated-tasks')
);
const PlanogramValidatedTaskDetailPage = lazy(
  () => import(/* webpackChunkName: "planogram-validated-task-detail" */ '@/pages/planogram/validated-task-detail')
);

const routes: RouteObject[] = [
  // PROTECTED ROUTES --------------------------------------------------------------------------------------------------
  {
    element: <Layout showMenu={true} showHeader={false} />,
    children: [
      // PROTECTED INDEX -----------------------------------------------------------------------------------------------
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: ROUTE_PATHS.rootLogged,
        element: <HomePage />,
      },
      {
        path: ROUTE_PATHS.cabinets.new,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <AddCabinetPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.cabinets.edit,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <EditCabinetPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.cabinets.assign,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <AssignCabinetPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.cabinets.main,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <CabinetsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.cabinets.assigned,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <CabinetsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.cabinets.unassigned,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_DATASETS_REVIEWER}>
            <UnassignedCabinetsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.productQa.main,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <ProductQAPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.productQa.edit,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <EditProductPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.datasets.main,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_DATASETS_REVIEWER}>
                <DatasetsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.datasets.assignedTask,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_DATASETS_REVIEWER}>
                <DatasetReviewPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: ROUTE_PATHS.terminals.main,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <FindTerminalsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.terminals.edit,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <EditTerminalPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.terminals.create,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <CreateTerminalPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.terminals.find,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <FindTerminalsPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: ROUTE_PATHS.terminals.create,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <CreateTerminalPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.fallback.main,
        children: [
          {
            path: ROUTE_PATHS.fallback.validationProfiles,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <ValidationProfilesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.fallback.createValidationProfile,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <CreateValidationProfilePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.fallback.editValidationProfile,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <EditValidationProfilePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.fallback.fallbackUsers,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <FallbackUsersPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.fallback.editFallbackUser,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <EditFallbackUserPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.fallback.sessions,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <SessionsPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: ROUTE_PATHS.profile.edit,
        element: <EditProfilePage />,
      },
      {
        path: ROUTE_PATHS.storeProject.main,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <StoreProjectPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.storeProject.new,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <CreateStoreProjectPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.storeProject.edit,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <EditStoreProjectPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.senseiUsers.main,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <SenseiUsersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.senseiUsers.create,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <CreateSenseiUserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.senseiUsers.edit,
        element: (
          <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
            <EditSenseiUserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTE_PATHS.planogram.main,
        children: [
          {
            path: ROUTE_PATHS.planogram.tasks,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <PlanogramTasksPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.planogram.assignedTask,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <PlanogramTaskDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.planogram.validatedTasks,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <PlanogramValidatedTasksPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ROUTE_PATHS.planogram.validatedTaskDetail,
            element: (
              <ProtectedRoute requiredRole={UserAuthorityRole.BO_ADMIN}>
                <PlanogramValidatedTaskDetailPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
  },

  // PUBLIC ROUTES -----------------------------------------------------------------------------------------------------
  {
    element: <Layout showHeader={false} publicRoute />,
    children: [
      // PUBLIC INDEX --------------------------------------------------------------------------------------------------
      {
        index: true,
        element: <PageLogin />,
      },
      // LOGIN ---------------------------------------------------------------------------------------------------------
      {
        path: ROUTE_PATHS.login,
        element: <PageLogin />,
      },
      // FORGOT PASSWORD ------------------------------------------------------------------------------------------------
      {
        path: ROUTE_PATHS.auth.forgotPassword,
        element: <ForgotPasswordPage />,
      },
      // FORGOT PASSWORD CONFIRMATION ----------------------------------------------------------------------------------
      {
        path: ROUTE_PATHS.auth.forgotPasswordConfirmation,
        element: <ForgotPasswordConfirmationPage />,
      },
      // SET PASSWORD ----------------------------------------------------------------------------------------------
      {
        path: ROUTE_PATHS.auth.setPassword,
        element: <SetPasswordPage />,
      },
      // SET NEW USER PASSWORD ----------------------------------------------------------------------------------------------
      {
        path: ROUTE_PATHS.auth.setNewUserPassword,
        element: <SetNewUserPasswordPage />,
      },
      {
        path: ROUTE_PATHS.auth.setPasswordConfirmation,
        element: <SetPasswordConfirmationPage />,
      },
    ],
  },
  {
    element: <Layout publicRoute />,
    children: [
      // GENERAL -------------------------------------------------------------------------------------------------------
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
  },
];

export { ROUTE_PATHS };

export default routes;
